import { SkeletonLoader } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useCompanyRegionExposure } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '~/routes/query-params';

import { GeoExposureDetails } from '../components/geo-exposure-details/GeoExposureDetails';
import * as S from '../components/geo-exposure-details/GeoExposureDetails.styles';

export interface RegionExposureDrawerContentProps {
  entity: Entity;
}

export const RegionExposureDrawerContent = ({
  entity,
}: RegionExposureDrawerContentProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const regionName = params[queryKeys.regionName];

  const { data, isFetching, isError, error } = useCompanyRegionExposure({
    entityTag: entity.tag,
  });

  const selected = useMemo(
    () => data?.find(item => item.name === regionName),
    [data, regionName]
  );

  if (isFetching) {
    return <SkeletonLoader areas={S.loadingSkeletonAreas} />;
  }

  if (isError || !selected || !regionName) {
    throw new Error(error?.message || 'Region data not found');
  }

  return (
    <GeoExposureDetails
      entity={entity}
      targetName={regionName}
      targetType="region"
      title={t(
        'widget:assetOverview.knowledgeGraph.geographicExposure.drawer.companyRegionExposure',
        {
          entity: entity.name_short || entity.name,
        }
      )}
      rankTitle={t(
        'widget:assetOverview.knowledgeGraph.geographicExposure.drawer.regionExposureRank'
      )}
      description={selected.relationship.description}
      rank={selected.relationship.rank}
      evidence={selected.relationship.evidence}
      sourceEvidence={selected.relationship.source_evidence}
    />
  );
};
